import React, { useEffect, useState } from 'react';
import { ownerListKeys, ownerCreateKey, ownerUpdateKey, ownerDeleteKey } from '../lib/api';
import { toast } from 'sonner';
import { Key, Plus, Trash2, Eye, EyeOff, Shield, Check, X } from 'lucide-react';
import { Switch } from './ui/switch';

export default function OwnerApiKeys() {
  const [keys, setKeys] = useState([]);
  const [form, setForm] = useState({ service: '', label: '', value: '', admin_visible: false });
  const [reveal, setReveal] = useState({});     // id -> bool
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setKeys(await ownerListKeys()); } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service.trim() || !form.label.trim() || !form.value.trim()) return toast.error('All fields required');
    try {
      const k = await ownerCreateKey(form);
      setKeys(list => [k, ...list]);
      setForm({ service: '', label: '', value: '', admin_visible: false });
      toast.success('Key saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const toggleVisible = async (k) => {
    try {
      const updated = await ownerUpdateKey(k.id, { admin_visible: !k.admin_visible });
      setKeys(list => list.map(x => x.id === k.id ? updated : x));
      toast.success(updated.admin_visible ? 'Admins can now view this key' : 'Hidden from admins');
    } catch (err) { toast.error('Failed'); }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this API key permanently?')) return;
    try { await ownerDeleteKey(id); setKeys(list => list.filter(x => x.id !== id)); toast.success('Deleted'); }
    catch (err) { toast.error('Failed'); }
  };

  return (
    <div data-testid="owner-apikeys-tab">
      <div className="card-shell p-5">
        <div className="flex items-center gap-2"><Key className="w-4 h-4 text-[#ffcf52]" /><h3 className="row-title text-white m-0">API keys & secrets</h3></div>
        <p className="text-white/60 text-[13px] mt-1">Only the Owner can see these keys by default. Toggle &quot;Admin visible&quot; to grant specific keys to Admins.</p>

        <form onSubmit={submit} className="grid sm:grid-cols-4 gap-3 mt-5">
          <input value={form.service} onChange={e => setForm({ ...form, service: e.target.value })} placeholder="service (adsense, stripe…)" className="input-field" data-testid="key-service" />
          <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="label (e.g. Production)" className="input-field" data-testid="key-label" />
          <input value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="key / secret" className="input-field font-mono" data-testid="key-value" />
          <div className="flex items-center gap-2">
            <Switch checked={form.admin_visible} onCheckedChange={v => setForm({ ...form, admin_visible: v })} />
            <span className="text-[13px] text-white/75">Admin visible</span>
          </div>
          <button className="btn-primary sm:col-span-4" data-testid="key-submit"><Plus className="w-4 h-4" /> Store key</button>
        </form>
      </div>

      <div className="card-shell mt-5 overflow-hidden">
        {keys.length === 0 && !loading ? (
          <div className="p-10 text-center text-white/50 text-[13.5px]">No keys yet. Add one above to let integrations use it.</div>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead className="bg-white/[0.03] text-white/55">
              <tr>
                <th className="text-left px-5 py-2">Service</th>
                <th className="text-left px-3 py-2">Label</th>
                <th className="text-left px-3 py-2">Value</th>
                <th className="text-center px-3 py-2">Admin access</th>
                <th className="px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`key-row-${k.service}`}>
                  <td className="px-5 py-3 capitalize font-semibold">{k.service}</td>
                  <td className="px-3 py-3 text-white/75">{k.label}</td>
                  <td className="px-3 py-3 font-mono text-[12.5px] text-white/85">
                    {reveal[k.id] ? k.value : '•'.repeat(Math.min(k.value?.length || 8, 24))}
                    <button onClick={() => setReveal(r => ({ ...r, [k.id]: !r[k.id] }))} className="ml-2 text-white/45 hover:text-white align-middle" aria-label="Toggle reveal">
                      {reveal[k.id] ? <EyeOff className="w-3.5 h-3.5 inline" /> : <Eye className="w-3.5 h-3.5 inline" />}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => toggleVisible(k)} className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border"
                      style={{ background: k.admin_visible ? 'rgba(34,211,238,0.15)' : 'rgba(255,255,255,0.05)', borderColor: k.admin_visible ? 'rgba(34,211,238,0.4)' : 'rgba(255,255,255,0.12)', color: k.admin_visible ? '#22d3ee' : 'rgba(255,255,255,0.65)' }}>
                      {k.admin_visible ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {k.admin_visible ? 'Shared with admins' : 'Owner only'}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => doDelete(k.id)} className="text-white/60 hover:text-[#ff6b8a]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 text-[12.5px] text-white/45 flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Admins access allowed keys via <span className="font-mono text-white/70">GET /api/admin/apikeys</span>.</div>
    </div>
  );
}
